import { apiFetch } from '../utils/apiFetch';
import { API_CONFIG, QUESTIONNAIRE_TYPE } from '../constants';
import { mapQuestionnaireResponseToCategory } from '../utils/helpers';
import { QuestionnaireResponse, type Question } from '../dtos/questionnaire';

export type QuestionnaireType = typeof QUESTIONNAIRE_TYPE[keyof typeof QUESTIONNAIRE_TYPE];

export interface AssessmentCategory {
    id: number;
    name: QuestionnaireType;
    title: string;
    questions: Question[];
    questionCount: number;
}

export interface CategoryGroup {
    type: QuestionnaireType;
    assessments: AssessmentCategory[];
}

/**
 * Maps QUESTIONNAIRE_TYPE values to their localization keys.
 * Use t(GROUP_TITLE_KEYS[group.type]) to display localized group titles.
 */
export const GROUP_TITLE_KEYS: Record<QuestionnaireType, string> = {
    [QUESTIONNAIRE_TYPE.MENTAL_HEALTH]: 'assessment.groups.mentalHealth',
    [QUESTIONNAIRE_TYPE.MEDICAL_RISK]: 'assessment.groups.medicalRisk',
    [QUESTIONNAIRE_TYPE.WELLBEING]: 'assessment.groups.wellbeing',
    [QUESTIONNAIRE_TYPE.ORGANIZATIONAL_PSYCHOLOGY]: 'assessment.groups.orgPsychology',
};

const toArray = <T,>(value: T | T[] | null | undefined): T[] => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
};

export const fetchQuestionnaire = async (
    type: QuestionnaireType,
    lang: string = localStorage.getItem('app-language') ?? 'ar'
): Promise<QuestionnaireResponse> => {
    try {
        const response = await apiFetch({
            url: API_CONFIG.ENDPOINTS.QUESTIONNAIRE,
            method: 'GET',
            params: { category: type, lang },
            headers: {
                "Authorization": `Bearer ${process.env.REACT_APP_QUESTIONNAIRE_TOKEN}`
            }
        });
        return await response.json();
    } catch (error) {
        console.error('Error fetching questionnaire:', error);
        throw error;
    }
};

/**
 * Fetches all four questionnaire categories and returns them as grouped data.
 * Each CategoryGroup contains the individual assessments (questionnaires) within it.
 */
export const fetchCategoryGroups = async (
    lang: string = localStorage.getItem('app-language') ?? 'ar'
): Promise<CategoryGroup[]> => {
    const [wellbeing, mentalHealth, medicalRisk, organizationalPsychology] = await Promise.all([
        fetchQuestionnaire(QUESTIONNAIRE_TYPE.WELLBEING, lang),
        fetchQuestionnaire(QUESTIONNAIRE_TYPE.MENTAL_HEALTH, lang),
        fetchQuestionnaire(QUESTIONNAIRE_TYPE.MEDICAL_RISK, lang),
        fetchQuestionnaire(QUESTIONNAIRE_TYPE.ORGANIZATIONAL_PSYCHOLOGY, lang),
    ]);

    const raw: [QuestionnaireType, QuestionnaireResponse][] = [
        [QUESTIONNAIRE_TYPE.MENTAL_HEALTH, mentalHealth],
        [QUESTIONNAIRE_TYPE.MEDICAL_RISK, medicalRisk],
        [QUESTIONNAIRE_TYPE.WELLBEING, wellbeing],
        [QUESTIONNAIRE_TYPE.ORGANIZATIONAL_PSYCHOLOGY, organizationalPsychology],
    ];

    return raw.map(([type, response]) => ({
        type,
        assessments: toArray(mapQuestionnaireResponseToCategory(response, type)).filter(Boolean) as AssessmentCategory[],
    }));
};

/** Returns a flat list of all assessments across all groups. */
export const fetchAllCategories = async (
    lang: string = localStorage.getItem('app-language') ?? 'ar'
): Promise<AssessmentCategory[]> => {
    const groups = await fetchCategoryGroups(lang);
    return groups.flatMap(g => g.assessments);
};
